import { useEffect, useRef, useState } from "react";
import useLanguage from "../../hooks/language";
import styles from "./appointment-hold-banner.module.css";

type HoldTimerBannerProps = {
    expiry: string;
    variant?: "default" | "bookingFlow";
    onExpired?: () => void;
    /** When true, do not fire onExpired (e.g. checkout in progress). */
    pauseExpiry?: boolean;
};

export const getRemainingHoldTimeInSeconds = (expiry: string): number => {
    const msRemaining = new Date(expiry).getTime() - Date.now();
    return Math.max(0, Math.floor(msRemaining / 1000));
};

const HoldTimerBanner: React.FC<HoldTimerBannerProps> = ({
    expiry,
    variant = "default",
    onExpired,
    pauseExpiry = false,
}) => {
    const { translations } = useLanguage();
    const [, forceRender] = useState(0);
    const expiredHandledRef = useRef(false);

    useEffect(() => {
        expiredHandledRef.current = false;
    }, [expiry]);

    useEffect(() => {
        if (!pauseExpiry) {
            expiredHandledRef.current = false;
        }
    }, [pauseExpiry]);

    useEffect(() => {
        const fireExpiredOnce = () => {
            if (pauseExpiry || expiredHandledRef.current || !onExpired) {
                return;
            }
            if (getRemainingHoldTimeInSeconds(expiry) > 0) {
                return;
            }
            expiredHandledRef.current = true;
            onExpired();
        };

        fireExpiredOnce();

        const interval = setInterval(() => {
            fireExpiredOnce();
            forceRender((t) => t + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [expiry, onExpired, pauseExpiry]);

    const remainingSeconds = getRemainingHoldTimeInSeconds(expiry);
    const minutes = String(Math.floor(remainingSeconds / 60)).padStart(2, "0");
    const seconds = String(remainingSeconds % 60).padStart(2, "0");

    return (
        <div
            className={`${styles["appointment-hold-banner"]} ${
                variant === "bookingFlow"
                    ? styles["appointment-hold-banner-booking-flow"]
                    : ""
            }`}
        >
            <div className={styles["appointment-hold-banner-content-wrapper"]}>
                <span className={styles["appointment-hold-countdown"]}>
                    {minutes}:{seconds}
                </span>
                <span>{translations.appointmentHoldBanner.message}</span>
            </div>
        </div>
    );
};

export default HoldTimerBanner;
